<script lang="ts" setup>
import { PropertyTypeDTO, PropertyTypeProperty } from '@/core/collections/contracts-api/companies/property-types/property-type.interface';
import { mapPropertyTypeFormErrors } from '@/core/collections/contracts-api/companies/property-types/property-type.schema';

//#region Globals
const { state } = useStore();
//#endregion

//#region Props
const props = defineProps({
   loading: { type: Boolean, default: false },
   errors: { type: Object as PropType<{ [key: string]: string }>, default: () => ({}) },
});
//#endregion

//#region ModelValue
const formObj = defineModel<Partial<PropertyTypeDTO>>('modelValue', { required: true });

const displayErrors = computed(() => mapPropertyTypeFormErrors(props.errors));
//#endregion

//#region Disabled
const permissions = computed(() => state.permission?.permissions?.customers);

const disabled = computed(() => {
   if (props.loading) return true;

   return !checkAccess('update', permissions.value);
});
//#endregion
</script>

<template>
   <div :class="'company-property-form'">
      <NxtInput
         v-model="formObj.name"
         :name="'name'"
         :label="$t('generic.name')"
         :errors="displayErrors"
         :disabled="disabled"
         :required="true"
      />

      <h3 class="company-property-form__add-options">{{ $t('actions.add-options', 2) }}</h3>

      <ul class="company-property-form__properties">
         <li v-for="(property, index) in formObj.company_properties" :key="index" class="company-property-form__properties__property">
            <NxtInput
               v-model="property.name"
               :name="`property-${index}-name`"
               :label="`${$t(`generic.option`)} ${index + 1}`"
               :errors="displayErrors"
               :disabled="disabled"
               :required="true"
            />

            <NxtIconButton
               v-if="(formObj.company_properties || []).length > 1"
               :name="'trash'"
               :disabled="disabled || property.is_used"
               :hover-text="property.is_used ? $t('generic.feedback.in-use') : $t('actions.delete-type', { type: $t('generic.option') })"
               @on-click="formObj.company_properties?.splice(index, 1)"
            />
         </li>

         <li key="add-company-property" class="mt-3">
            <NxtLink @on-click="formObj.company_properties?.push({ name: '' } as PropertyTypeProperty)">
               + {{ $t('actions.add-options') }}
            </NxtLink>
         </li>
      </ul>
   </div>
</template>

<style lang="scss" scoped>
.company-property-form {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter-large);
}

.company-property-form__add-options {
   margin: 0;
}

.company-property-form__properties {
   padding: 0;
   list-style: none;
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
   margin-top: 0;
}

.company-property-form__properties__property {
   display: flex;
   align-items: center;
   gap: var(--nxt-gutter);
   width: 100%;

   > :first-child {
      flex: 1;
   }
}
</style>
